/**
 * NPC Config
 *
 * Global defaults for NPC dimensions and respawn timing. Per-NPC overrides
 * (width/height/respawn for a specific NPC type) live in `EntityConfig.ts`
 * and the per-NPC TOML files — this config only sets the fallback used
 * when a specific NPC doesn't declare its own.
 *
 * Two-section file (defaults+dev-override pattern). See
 * `docs/plans/2026-04-25-CONFIG-MIGRATION.md`.
 */

export interface NpcConfig {
  dimensions: {
    /** Default sprite width in pixels for NPCs without an explicit `width`. */
    defaultWidth: number;
    /** Default sprite height in pixels for NPCs without an explicit `height`. */
    defaultHeight: number;
  };
  timing: {
    /** Milliseconds between an NPC despawn and its respawn. */
    respawnDelayMs: number;
  };
}

export const NPC_DEFAULTS_CONFIG: NpcConfig = {
  dimensions: {
    defaultWidth: 60,
    defaultHeight: 50,
  },
  timing: {
    respawnDelayMs: 5000,
  },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through
 * to NPC_DEFAULTS_CONFIG.
 *
 * Examples:
 *   timing: { respawnDelayMs: 1000 }     // fast respawn for combat QA
 *   dimensions: { defaultWidth: 100 }    // big NPCs for screenshot framing
 */
export const NPC_DEV_OVERRIDE: Partial<NpcConfig> = {};

export const NPC: NpcConfig = {
  dimensions: { ...NPC_DEFAULTS_CONFIG.dimensions, ...NPC_DEV_OVERRIDE.dimensions },
  timing: { ...NPC_DEFAULTS_CONFIG.timing, ...NPC_DEV_OVERRIDE.timing },
};
